import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { chapters, STORY_ARTICLE } from "@/data/content";
import { Eyebrow, Reveal, SectionTitle } from "./Reveal";
import { Motto } from "./Motto";

const Chapter = ({ c, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = i % 2 === 1;
  return (
    <article ref={ref} data-testid={`story-chapter-${i + 1}`}
      className="grid md:grid-cols-12 gap-8 md:gap-12 items-center py-16 sm:py-24 border-t border-black/10">
      <div className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}>
        <Reveal>
          <div className={`relative ${c.ratio || "aspect-[4/5]"} overflow-hidden`}>
            {c.ratio ? (
              <img src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <motion.img style={{ y, objectPosition: c.pos || "50% 50%" }} src={c.img} alt={c.title}
                className="absolute inset-0 h-[116%] w-full -top-[8%] object-cover" />
            )}
          </div>
        </Reveal>
      </div>
      <div className={`md:col-span-6 ${flip ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
        <Reveal>
          <div className="flex items-baseline gap-5 mb-6">
            <span className="font-display italic text-6xl sm:text-7xl text-[var(--gold)]">{c.n}</span>
            <Eyebrow>{c.tag}</Eyebrow>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mb-6">{c.title}</h3>
          <p className="text-base sm:text-lg text-stone-600 font-light leading-relaxed max-w-xl">{c.body}</p>
          {c.note && <p className="mt-5 text-sm text-stone-500 italic">{c.note}</p>}
        </Reveal>
      </div>
    </article>
  );
};

export const Story = () => (
  <section id="story" data-testid="life-story-section" className="relative max-w-[1500px] mx-auto px-5 sm:px-10 pt-36 sm:pt-44 pb-10">
    <Reveal className="grid md:grid-cols-12 gap-8 mb-10">
      <div className="md:col-span-4"><Eyebrow>Story</Eyebrow></div>
      <div className="md:col-span-8">
        <SectionTitle>
          Mimi Tao: <em className="text-[var(--gold)]">The Cinematic Odyssey</em>
        </SectionTitle>
      </div>
    </Reveal>
    {chapters.map((c, i) => <Chapter key={c.n} c={c} i={i} />)}
    <Motto testid="story-motto" className="border-t border-black/10" />
    <Reveal className="border-t border-black/10 py-16 sm:py-20 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
      <div>
        <Eyebrow className="mb-4">Read the full story</Eyebrow>
        <p className="font-display text-2xl sm:text-4xl leading-tight max-w-2xl">The monk-turned-model breaking all the rules</p>
        <p className="mt-3 text-xs uppercase tracking-[0.25em] text-stone-600">Bangkok Post · Special Report</p>
      </div>
      <a data-testid="story-bangkok-post-link" href={STORY_ARTICLE} target="_blank" rel="noreferrer"
        className="group inline-flex items-center gap-3 self-start sm:self-auto rounded-full bg-[var(--ink)] text-[var(--bg)] px-7 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] transition-colors duration-500">
        Read on Bangkok Post <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
      </a>
    </Reveal>
  </section>
);
